import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import CheckBoxFormik from "../../../components/UI/Checkbox/CheckBoxFormik";

const validationSchema = Yup.object().shape({
  sqTipoParteServico: Yup.string().default(""),
  codigo: Yup.string().default(""),
  nome: Yup.string()
    .ensure()
    .default("")
    .required("Informe o Nome do Tipo de Parte de Serviço")
    .max(200, "A descrição não pode ter mais de 200 caracteres"),
  sigla: Yup.string()
    .ensure()
    .default("")
    .required("Informe a Descrição da Fase")
    .max(1, "A SIGLA não pode ter mais de 1 caractere"),
  status: Yup.string(),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  if (key === "status") {
    initialValues[key] = true;
  } else {
    initialValues[key] = "";
  }
});

const FormTipoParteServico = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sqTipoParteServico)
        submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraTipoParteServico, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiTipoParteServico, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { values, setSubmitting, setValues, setFieldValue } = formik;

  useEffect(() => {
    if (submitIsLoading) return;

    if (submitData || submitErrors) setSubmitting(false);

    if (submitData) {
      toast.success("Tipo de Parte de Serviço salvo com sucesso");
      history.push("/app/tipoparteservico");
    }
  }, [setSubmitting, submitData, submitErrors, submitIsLoading, history]);

  // Carregar item
  const {
    request: ocorrenciaRequest,
    data: ocorrenciaData,
    errors: ocorrenciaErrors,
    isLoading: ocorrenciaIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      setSubmitting(true);
      ocorrenciaRequest(apiRoutes.fiscalizacao.parametrizacao.consultaTipoParteServico, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [ocorrenciaRequest, setSubmitting, state]);

  useEffect(() => {
    if (ocorrenciaIsLoading) return;

    setSubmitting(false);

    if (ocorrenciaErrors) {
      toast.error(ocorrenciaErrors.message);
    }

    if (!values.sqTipoOcorrencia && ocorrenciaData && ocorrenciaData.result.length) {
      const info = {
        codigo: ocorrenciaData?.result[0]?.sqTipoParteServico,
        data: {
          nomeFormalInclusao: ocorrenciaData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: ocorrenciaData?.result[0]?.nomeFormalAlteracao,
          documentoInclusao: ocorrenciaData?.result[0]?.usuarioInclusao,
          documentoAlteracao: ocorrenciaData?.result[0]?.usuarioAlteracao,
          dataInclusao: ocorrenciaData?.result[0]?.dataInclusao,
          dataAlteracao: ocorrenciaData?.result[0]?.dataAlteracao,
          status: ocorrenciaData?.result[0]?.status === "S",
        },
      };

      setValues({ ...ocorrenciaData.result[0] });
      setDadosInfo(info);
      setFieldValue("status", ocorrenciaData?.result[0]?.status === "S");
    }
  }, [
    setValues,
    setSubmitting,
    ocorrenciaData,
    ocorrenciaErrors,
    ocorrenciaIsLoading,
    values.sqTipoOcorrencia,
    setFieldValue,
  ]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Tipo de Parte de Serviço</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sqTipoParteServico
                      ? "Alteração de Tipo de Parte de Serviço"
                      : "Novo Tipo de Parte de Serviço"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="codigo" />
                </Col>
              </Row>

              {/* <Button href="#" className="btn btn-primary btn-sm btn-user">Botão</Button> */}
            </div>
            <div className="card-body">
              <FormAlert formik={formik} data={submitData} errors={submitErrors} isLoading={submitIsLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sqTipoParteServico
                      ? !isAllowed(funcionalidades.tipoParteServico.alterar)
                      : !isAllowed(funcionalidades.tipoParteServico.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12} md={2}>
                      <Form.Label>Sigla</Form.Label>
                      <TextInputFormik formik={formik} field="sigla" maxLength="200" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="nome" maxLength="4000" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={4}>
                      <CheckBoxFormik
                        style={{ paddingTop: 40 }}
                        inline="inline"
                        formik={formik}
                        field="status"
                        label="Ativo"
                      />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando fase..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sqTipoParteServico
                            ? funcionalidades.tipoParteServico.alterar
                            : funcionalidades.tipoParteServico.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.codigo && !formik.values.sqTipoParteServico && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormTipoParteServico;
